'use client'

import { requestPasswordReset } from '@repo/auth/client'
import { useEffect, useState } from 'react'

import {
  AUTH_ACCENT_GLOW,
  AUTH_ACCENT_GRADIENT,
  authInputClass,
} from '@/components/molecules/auth-card'
import { useToast } from '@/components/providers/ToastProvider'
import { BODY, CARD_TITLE, META } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * "Zapomniałeś hasła?" — the reset request, as a modal over the sign-in card.
 *
 * Lifted out of `login-page.client.tsx`, whole and unchanged in behaviour. That
 * file had grown to 556 lines holding two unrelated screens; this one is opened
 * by a single button and closes over nothing the card needs.
 *
 * It is opened WITH the address already in the box, read at the moment of the
 * click — somebody who mistyped their password should not retype their e-mail.
 */
export function ForgotPasswordModal({
  open,
  onClose,
  defaultEmail,
}: {
  open: boolean
  onClose: () => void
  defaultEmail: string
}) {
  const [email, setEmail] = useState(defaultEmail)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    if (!open) return
    setEmail(defaultEmail)
    setSent(false)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, defaultEmail, onClose])

  if (!open) return null

  const valid = /\S+@\S+\.\S+/.test(email)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid || busy) return
    setBusy(true)
    // BetterAuth returns a generic success even for an unknown address, so this
    // never reveals whether an account exists. The reset link is delivered by
    // @repo/email (server-side sendResetPassword hook). `redirectTo` is where
    // BetterAuth sends the browser after it validates the token — the reset
    // page (P7), which reads `?token=` and sets the new password.
    await requestPasswordReset({ email, redirectTo: '/pl/reset-password' })
    setBusy(false)
    setSent(true)
    toast({ kind: 'success', title: 'Link wysłany', description: `Sprawdź ${email}` })
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Resetowanie hasła"
      className="flou-modal-backdrop fixed inset-0 z-[90] flex items-center justify-center bg-black/55 px-4 py-6 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="flou-modal-card relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 p-8 text-white shadow-[0_30px_120px_-20px_rgba(0,0,0,0.7)]"
        style={{
          background:
            'linear-gradient(165deg, oklch(0.16 0.018 280 / 0.97) 0%, oklch(0.07 0.008 280 / 0.99) 100%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij"
          className="absolute top-3 right-3 rounded-full p-2 text-white/55 transition-colors hover:bg-white/8 hover:text-white"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {sent ? (
          <div className="flex flex-col items-center text-center">
            <div
              aria-hidden
              className="mb-3 flex h-12 w-12 items-center justify-center rounded-full text-white"
              style={{ background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 7l9 6 9-6M3 7v10h18V7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className={CARD_TITLE}>Link został wysłany</h2>
            <p className={cn(BODY, 'mt-2 text-white/65')}>
              Sprawdź skrzynkę <span className="text-white">{email}</span>, instrukcję resetu hasła
              otrzymasz w ciągu kilku minut.
            </p>
            <button
              type="button"
              onClick={onClose}
              className={cn(
                META,
                'mt-5 inline-flex items-center justify-center rounded-full px-5 py-2.5 font-semibold text-white',
              )}
              style={{ background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }}
            >
              Zamknij
            </button>
          </div>
        ) : (
          <>
            <h2 className={CARD_TITLE}>Zresetuj hasło</h2>
            <p className={cn(META, 'mt-1 text-white/55')}>
              Wpisz adres e-mail. Wyślemy Ci link do ustawienia nowego hasła.
            </p>
            <form className="mt-5 flex flex-col gap-3" onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ty@example.com"
                autoComplete="email"
                className={cn(authInputClass, 'flou-auth-field')}
                autoFocus
              />
              <button
                type="submit"
                disabled={!valid || busy}
                className={cn(
                  BODY,
                  'w-full rounded-xl py-3 font-semibold text-white transition-all duration-300',
                  valid && !busy ? 'hover:brightness-110' : 'cursor-not-allowed opacity-50',
                )}
                style={{ background: AUTH_ACCENT_GRADIENT, boxShadow: AUTH_ACCENT_GLOW }}
              >
                {busy ? 'Wysyłam…' : 'Wyślij link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
